import { DebouncedInputField } from "@/components/DebouncedInputField";
import { CheckboxContainer } from "@/components/layout/CheckboxContainer";
import { CheckListContainer } from "@/components/layout/ChecklistContainer";
import { type QuestionsProps } from "@/features/questionnaire/types";
import { getTranslation } from "@/features/questionnaire/utils";
import styled from "@emotion/styled";
import { Switch, Variables } from "@repo/skapa";
import { useSettings } from "@repo/ui";
import { type ChangeEvent, useState } from "react";

const FreeTextContainer = styled.div`
  margin-top: ${Variables.space100};
`;

export const YesorNoQuestion = ({
  answer,
  onAnswerIdAnswered,
  question,
  onFreeTextAnswered,
}: QuestionsProps) => {
  const { language } = useSettings();
  const [extraInputValues, setExtraInputValues] = useState<
    Record<string, string>
  >(answer?.answerFreeText ? JSON.parse(answer.answerFreeText) : {});

  const selectedAnswerIds = answer?.answerIds || [];

  if (!question?.answers) {
    return null;
  }

  const handleOptionChange = (option: string, isChecked: boolean) => {
    const updatedSelectedIds = isChecked
      ? [...selectedAnswerIds, option]
      : selectedAnswerIds.filter((id: string) => id !== option);
    onAnswerIdAnswered?.(updatedSelectedIds);
  };

  const handleExtraInputChange = (answerId: string, value: string) => {
    const updatedValues = {
      ...extraInputValues,
      [answerId]: value,
    };
    setExtraInputValues(updatedValues);
    onFreeTextAnswered?.(JSON.stringify(updatedValues));
  };

  return (
    <div>
      {question.answers.map((option, index) => (
        <CheckListContainer key={option.answer_id_key ?? `index-${index}`}>
          <CheckboxContainer key={option.answer_id_key ?? `index-${index}`}>
            <Switch
              label={getTranslation(
                option.answer_translation ?? [],
                language,
                option.answer_name
              )}
              id={`${question.question_id_key}-${option.answer_id_key}`}
              key={`${question.question_id_key}-${option.answer_id_key}`}
              value={option.answer_id_key}
              checked={selectedAnswerIds.includes(option.answer_id_key)}
              subtle
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleOptionChange(option.answer_id_key, e.target.checked)
              }
            />
            {selectedAnswerIds.includes(option.answer_id_key) &&
              option.answer_extra_inputs && (
                <FreeTextContainer>
                  {option.answer_extra_inputs.map((extraInput) => (
                    <DebouncedInputField
                      key={`${option.answer_id_key}-${extraInput.answer_extra_inputs_id_key}`}
                      label={getTranslation(
                        extraInput.answer_extra_inputs_translation ?? [],
                        language,
                        extraInput.answer_extra_input_name || ""
                      )}
                      value={
                        extraInputValues[
                          `${option.answer_id_key}-${extraInput.answer_extra_inputs_id_key}`
                        ] || ""
                      }
                      onChange={(value) =>
                        handleExtraInputChange(
                          `${option.answer_id_key}-${extraInput.answer_extra_inputs_id_key}`,
                          value
                        )
                      }
                    />
                  ))}
                </FreeTextContainer>
              )}
          </CheckboxContainer>
        </CheckListContainer>
      ))}
    </div>
  );
};
